// src/utils/raceStats.ts
//
// Obsazenost závodu – veřejný dokument race_stats/{raceId}
// { registered, lastChange, updatedAt } (bezpečnost a, 3. 10. 2026).
//
// Cizí přihlášky už nejsou veřejně čitelné, takže obsazenost (UserDashboard,
// PaymentRace, kapacita) se nepočítá z kolekce registrations, ale z tohoto
// počítadla. Pravidla (firestore.rules) ho střelci dovolí posunout jen o +1
// ve stejné transakci, ve které zakládá svou přihlášku (lastChange = id
// přihlášky, do kapacity závodu), a o −1 jen se smazáním své přihlášky.
// Správce počítadlo udržuje při mazání přihlášek (AdminRegistrations) a umí
// ho přepočítat z přihlášek (recountRaceStats – AdminOverview, jeden závod
// recountRaceStatsFor – AdminRegistrations při nesouladu).
//
// Souběh: když jiný střelec posune počítadlo mezi čtením a commitem
// transakce, pravidla vyhodnotí zastaralé ±1 dřív než verzi dokumentu
// a vrátí permission-denied, které SDK neopakuje – transakce přihlášky
// a odhlášení se proto opakují samy (retryOnContention, s rostoucí náhodnou
// prodlevou). Nával, který nepovolí ani po opakováních, skončí RaceBusyError
// („hlásí se víc lidí najednou"), trvalé odmítnutí pravidly propadne hned.
//
// Kapacita: maxCapacity > 0 = limit, prázdné nebo 0 = neomezeno (nápověda
// formuláře závodu v Races.tsx; dřív PaymentRace bral 0 jako „plno").

import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  query,
  runTransaction,
  serverTimestamp,
  where,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";

export const RACE_STATS_COLLECTION = "race_stats";

export const raceStatsRef = (raceId: string): DocumentReference =>
  doc(db, RACE_STATS_COLLECTION, raceId);

/** Počet přihlášek z dokumentu počítadla; chybějící / nesmyslná hodnota → 0. */
export function registeredOf(data: DocumentData | undefined | null): number {
  const n = data ? data.registered : undefined;
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Kapacita závodu: kladné číslo, jinak null = neomezeno. */
export function capacityOf(race: { maxCapacity?: unknown } | null | undefined): number | null {
  const raw = race ? race.maxCapacity : undefined;
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/** Závod je plný: kapacita je nastavená a počet přihlášek ji dosáhl. */
export function isRaceFull(registered: number | null | undefined, capacity: number | null): boolean {
  return capacity !== null && registered != null && registered >= capacity;
}

/** Počet přihlášek závodu (chybějící počítadlo = 0 – závod bez přihlášek). */
export async function loadRegisteredCount(raceId: string): Promise<number> {
  const snap = await getDoc(raceStatsRef(raceId));
  return snap.exists() ? registeredOf(snap.data()) : 0;
}

/** Počty přihlášek více závodů (dotazy po 30 id); chybějící počítadlo = 0. */
export async function loadRegisteredCounts(
  raceIds: ReadonlyArray<string>
): Promise<Record<string, number>> {
  const ids = Array.from(new Set(raceIds.filter(Boolean)));
  const counts: Record<string, number> = {};
  ids.forEach((id) => {
    counts[id] = 0;
  });
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    const snap = await getDocs(
      query(collection(db, RACE_STATS_COLLECTION), where(documentId(), "in", chunk))
    );
    snap.docs.forEach((d) => {
      counts[d.id] = registeredOf(d.data());
    });
  }
  return counts;
}

/** Závod je plný – přihláška se nezaložila. */
export class RaceFullError extends Error {
  readonly code = "lks/race-full";
  constructor(readonly capacity: number) {
    super(`Závod je plně obsazen (${capacity} míst).`);
    this.name = "RaceFullError";
  }
}

/**
 * Závod mezitím někdo smazal – přihláška se nezaložila. Pravidla to střelci
 * nedovolí sama, správci ano (jeho větev pravidel existenci závodu
 * nekontroluje) – bez této kontroly by vznikla přihláška a obsazenost
 * k neexistujícímu závodu (stará otevřená stránka správce).
 */
export class RaceGoneError extends Error {
  readonly code = "lks/race-gone";
  constructor() {
    super("Závod už neexistuje.");
    this.name = "RaceGoneError";
  }
}

export type CreateRegistrationResult =
  | { created: true }
  | { created: false; existing: DocumentData };

/**
 * Přihláška nebo odhlášení prohrálo souběh o počítadlo i po všech
 * opakováních – na závod se zrovna hlásí víc střelců najednou (nával po
 * oznámení závodu). Nic se nezapsalo, stačí to zkusit znovu. Kód "aborted"
 * = souběžná změna (firebaseErrors: „…Zkuste to znovu."), message je hláška
 * pro střelce. Dřív propadlo permission-denied a střelec viděl „Na tuto akci
 * nemáte oprávnění", i když v závodě bylo místo.
 */
export class RaceBusyError extends Error {
  readonly code = "aborted";
  constructor() {
    super("Na závod se teď hlásí víc lidí najednou – zkuste to prosím znovu.");
    this.name = "RaceBusyError";
  }
}

/**
 * Kolikrát se transakce s počítadlem zopakuje po prohraném souběhu. Jeden
 * pokus jsou tři síťové okruhy (na mobilu 0,3–0,7 s) a v každém kole souběhu
 * projde jen jeden střelec – s rostoucí prodlevou (contentionDelay) projde
 * i nával 15 souběžných přihlášek (ověřeno na emulátoru s mobilní latencí).
 */
const CONTENTION_RETRIES = 5;

/** Horní mez prodlevy před prvním opakováním; každé další ji zdvojnásobí. */
const CONTENTION_DELAY_MS = 300;

const isPermissionDenied = (err: unknown): boolean =>
  typeof err === "object" &&
  err !== null &&
  (err as { code?: unknown }).code === "permission-denied";

/**
 * Náhodná prodleva před opakováním č. `retry` (od 0): 0 až 300·2^retry ms,
 * tj. meze 300, 600, 1200, 2400 a 4800 ms (dohromady nejvýš 9,3 s, jen při
 * trvajícím souběhu). Náhoda souběžné střelce rozhodí v čase, rostoucí mez
 * jim při návalu dá prostor – dřívější pevná prodleva 50–200 ms byla kratší
 * než samotný pokus a opakující se střelci se srazili znovu.
 */
const contentionDelay = (retry: number): Promise<void> =>
  new Promise((resolve) =>
    setTimeout(resolve, Math.floor(Math.random() * CONTENTION_DELAY_MS * 2 ** retry))
  );

/**
 * Otisk počítadla – každý posun (přihláška, odhlášení, přepočet) zapíše nový
 * updatedAt (čas serveru); chybějící dokument = "".
 */
const statsStamp = (snap: DocumentSnapshot | null | undefined): string => {
  if (!snap || !snap.exists()) return "";
  const at = snap.get("updatedAt");
  const time =
    at && typeof at.seconds === "number" ? `${at.seconds}.${at.nanoseconds}` : String(at);
  return `${String(snap.get("registered"))}|${String(snap.get("lastChange"))}|${time}`;
};

/**
 * Jeden pokus transakce s počítadlem. Po čteních (před zápisy) předá
 * `readState` otisk všeho, z čeho počítá zápisy (počítadlo, kapacita,
 * záznam na čekací listině).
 */
type ContentionAttempt<T> = (readState: (state: string) => void) => Promise<T>;

/**
 * Transakce s počítadlem race_stats, která prohrála souběh: jiný střelec
 * zapsal počítadlo mezi jejím čtením a commitem. Pravidla vyhodnotí zastaralé
 * ±1 dřív než verzi dokumentu a vrátí permission-denied, které SDK (na rozdíl
 * od „aborted"/„failed-precondition") neopakuje – střelec by viděl „nemáte
 * oprávnění" a o poslední místo by nedostal RaceFullError. Celá transakce se
 * proto zopakuje (až CONTENTION_RETRIES× s rostoucí náhodnou prodlevou)
 * a čerstvé čtení projde, nebo zjistí plný závod. Souběh, který nepovolí ani
 * po posledním opakování → RaceBusyError.
 *
 * Souběh se pozná podle otisku: commit, který souběh prohrál, odmítla změna
 * po jeho čtení, takže další pokus přečte jiný otisk. Stejný otisk = nikdo
 * nic nezměnil a pravidla zápis odmítají trvale (startovné neodpovídá
 * kategorii, zákaz nováčka) – původní permission-denied propadne hned při
 * prvním opakování, bez dalšího čekání (volající pak třeba ověří cenu).
 * Zamítnuté čtení (ještě před otiskem) a jiné chyby se neopakují.
 */
async function retryOnContention<T>(
  attempt: ContentionAttempt<T>,
  retry = 0,
  denied: { state: string; error: unknown } | null = null
): Promise<T> {
  const read: { state: string | null } = { state: null };
  try {
    return await attempt((state) => {
      read.state = state;
      // Od odmítnutého pokusu se nic nezměnilo – o souběh nejde.
      if (denied && denied.state === state) throw denied.error;
    });
  } catch (err) {
    if (denied && err === denied.error) throw err;
    if (!isPermissionDenied(err) || read.state === null) throw err;
    if (retry >= CONTENTION_RETRIES) throw new RaceBusyError();
    await contentionDelay(retry);
    return retryOnContention(attempt, retry + 1, { state: read.state, error: err });
  }
}

/**
 * Založí přihlášku registrations/{regId} (regId = raceId__uid) a ve STEJNÉ
 * transakci posune obsazenost o +1 a smaže případný záznam na čekací listině
 * (waitlist/{regId}). Kapacitu čte z aktuálního dokumentu závodu uvnitř
 * transakce – dva souběžní střelci ji nepřekročí (dřív se počet četl mimo
 * transakci). Existující přihláška (druhý tab, opakovaný klik) → vrátí ji.
 * Plný závod → RaceFullError. Prohraný souběh se zopakuje (retryOnContention),
 * trvající nával → RaceBusyError; permission-denied propadne, když pravidla
 * zápis odmítají trvale.
 */
export async function createRegistrationCounted(params: {
  raceId: string;
  regId: string;
  fields: DocumentData;
}): Promise<CreateRegistrationResult> {
  const { raceId, regId, fields } = params;
  const regRef = doc(db, "registrations", regId);
  const statsRef = raceStatsRef(raceId);
  const raceRef = doc(db, "races", raceId);
  const waitRef = doc(db, "waitlist", regId);
  return retryOnContention((readState) =>
    runTransaction(db, async (tx) => {
      // Všechna čtení před zápisy (pravidlo transakcí Firestore).
      const regSnap = await tx.get(regRef);
      if (regSnap.exists()) {
        return { created: false, existing: regSnap.data() } as CreateRegistrationResult;
      }
      const [statsSnap, raceSnap, waitSnap] = await Promise.all([
        tx.get(statsRef),
        tx.get(raceRef),
        tx.get(waitRef),
      ]);
      if (!raceSnap.exists()) throw new RaceGoneError();
      const registered = statsSnap.exists() ? registeredOf(statsSnap.data()) : 0;
      const capacity = capacityOf(raceSnap.data());
      if (isRaceFull(registered, capacity)) throw new RaceFullError(capacity as number);
      readState(`${statsStamp(statsSnap)}|${String(capacity)}|${String(waitSnap.exists())}`);
      tx.set(regRef, { ...fields, registeredAt: serverTimestamp() });
      tx.set(statsRef, {
        registered: registered + 1,
        lastChange: regId,
        updatedAt: serverTimestamp(),
      });
      if (waitSnap.exists()) tx.delete(waitRef);
      return { created: true } as CreateRegistrationResult;
    })
  );
}

/**
 * Smaže přihlášku a ve STEJNÉ transakci sníží obsazenost o 1 (lastChange =
 * id přihlášky). Závod bez počítadla (před přípravou dat) → jen smazání.
 * Neexistující přihláška → nic. Po jedné přihlášce: pravidla neumí −N v jedné
 * dávce (historické duplicity se mažou postupně). Prohraný souběh s jiným
 * střelcem se zopakuje (retryOnContention), trvající nával → RaceBusyError.
 */
export async function deleteRegistrationCounted(regId: string): Promise<boolean> {
  const regRef = doc(db, "registrations", regId);
  return retryOnContention((readState) =>
    runTransaction(db, async (tx) => {
      const regSnap = await tx.get(regRef);
      if (!regSnap.exists()) return false;
      const raceId = regSnap.get("raceId");
      const statsRef = typeof raceId === "string" && raceId ? raceStatsRef(raceId) : null;
      const statsSnap = statsRef ? await tx.get(statsRef) : null;
      readState(statsStamp(statsSnap));
      tx.delete(regRef);
      if (statsRef && statsSnap && statsSnap.exists()) {
        tx.set(statsRef, {
          registered: Math.max(0, registeredOf(statsSnap.data()) - 1),
          lastChange: regId,
          updatedAt: serverTimestamp(),
        });
      }
      return true;
    })
  );
}

export interface RecountResult {
  /** Počet závodů s počítadlem. */
  races: number;
  /** Závody, u kterých se počet změnil (dřív chyběl nebo nesouhlasil). */
  changed: number;
  /** Přihlášky na neexistující závod (počítadlo se jim nezakládá). */
  orphans: number;
}

export interface RaceRecountResult {
  /** Zapsaný počet přihlášek závodu. */
  registered: number;
  /** Počítadlo před přepočtem; null = dokument chyběl. */
  previous: number | null;
}

/** Kolikrát se přepočet jednoho závodu zkusí, když se počítadlo během počítání měnilo. */
const RECOUNT_ATTEMPTS = 3;

/** Kolik závodů přepočet všech závodů zapisuje zároveň. */
const RECOUNT_PARALLEL = 10;

/** Počítadlo už nese přesně tento počet (číslo – text "5" se přepíše). */
const statsHold = (snap: DocumentSnapshot | undefined, registered: number): boolean =>
  !!snap && snap.exists() && snap.get("registered") === registered;

/** Kód jako souběžná změna ve Firestore (firebaseErrors: „…Zkuste to znovu."). */
const recountBusy = (message: string): Error =>
  Object.assign(new Error(message), { code: "aborted" });

const isAborted = (err: unknown): boolean =>
  typeof err === "object" && err !== null && (err as { code?: unknown }).code === "aborted";

type RecountWrite = "written" | "unchanged" | "conflict";

/**
 * Zapíše přepočtený počet závodu v transakci, jen když má počítadlo pořád
 * otisk z `seen` – jinak by přepočet přepsal souběžnou přihlášku nebo
 * odhlášení ("conflict", počítá se znovu). Počet, který už sedí, se
 * nepřepisuje ("unchanged"); chybějící počítadlo se založí (i s 0).
 */
async function writeRecount(
  raceId: string,
  seen: DocumentSnapshot | undefined,
  registered: number
): Promise<RecountWrite> {
  const statsRef = raceStatsRef(raceId);
  return runTransaction<RecountWrite>(db, async (tx) => {
    const now = await tx.get(statsRef);
    if (statsStamp(now) !== statsStamp(seen)) return "conflict";
    if (statsHold(now, registered)) return "unchanged";
    tx.set(statsRef, {
      registered,
      lastChange: "recount",
      updatedAt: serverTimestamp(),
    });
    return "written";
  });
}

/**
 * Přepočet jednoho závodu: počítadlo, dotaz na přihlášky závodu a zápis
 * s kontrolou otisku; při souběžné změně znovu (nejvýš RECOUNT_ATTEMPTS×).
 */
async function recountOneRace(
  raceId: string
): Promise<RaceRecountResult & { written: boolean }> {
  const statsRef = raceStatsRef(raceId);
  const regsQuery = query(collection(db, "registrations"), where("raceId", "==", raceId));
  for (let attempt = 1; attempt <= RECOUNT_ATTEMPTS; attempt += 1) {
    // Pořadí je podstatné: počítadlo, pak dotaz (paralelně by změna mezi
    // dotazem a čtením počítadla prošla bez povšimnutí). Obojí ze serveru –
    // perzistentní cache by bez spojení vrátila neúplná nebo stará data.
    const before = await getDocFromServer(statsRef);
    const regsSnap = await getDocsFromServer(regsQuery);
    const registered = regsSnap.size;
    const outcome = await writeRecount(raceId, before, registered);
    if (outcome !== "conflict") {
      return {
        registered,
        previous: before.exists() ? registeredOf(before.data()) : null,
        written: outcome === "written",
      };
    }
  }
  throw recountBusy("Přihlášky se během přepočtu měnily.");
}

/**
 * Přepočet obsazenosti všech závodů z přihlášek – JEN SPRÁVCE (čte všechny
 * přihlášky). Spouští se při přípravě dat bezpečnosti (a) a při podezření
 * na rozjetí počítadla; idempotentní – „změněno N" při opakování znamená,
 * že se mezitím přihlašoval nebo odhlašoval někdo bez počítadla (starý
 * klient).
 *
 * Čte ze serveru (ne z perzistentní cache – bez spojení by počítal
 * z neúplných dat) a zapisuje PO ZÁVODECH s kontrolou otisku jako
 * recountRaceStatsFor, takže souběžnou přihlášku ani odhlášení nepřepíše:
 * počítadla se přečtou před přihláškami a znovu po nich. Závod se stejným
 * otiskem před i po se mezitím neměnil (přihláška i odhlášení posouvají
 * počítadlo v téže transakci) – jeho počet se zapíše, jen když nesedí,
 * a v transakci jen při pořád stejném otisku. Závod, kterému se počítadlo
 * během počítání (nebo před zápisem) změnilo, se přepočítá samostatně.
 * Chybějící počítadlo se založí (i 0), počítadla smazaných závodů se smažou.
 * Závod, jehož přihlášky se měnily i při opakování, ostatní nezastaví –
 * přepočet pak skončí chybou „aborted" (spustit znovu).
 */
export async function recountRaceStats(): Promise<RecountResult> {
  const statsCol = collection(db, RACE_STATS_COLLECTION);
  // Pořadí: počítadla → závody a přihlášky → počítadla znovu.
  const before = await getDocsFromServer(statsCol);
  const [racesSnap, regsSnap] = await Promise.all([
    getDocsFromServer(collection(db, "races")),
    getDocsFromServer(collection(db, "registrations")),
  ]);
  const after = await getDocsFromServer(statsCol);

  const counts = new Map<string, number>();
  racesSnap.docs.forEach((d) => counts.set(d.id, 0));
  let orphans = 0;
  regsSnap.docs.forEach((d) => {
    const raceId = d.get("raceId");
    if (typeof raceId === "string" && counts.has(raceId)) {
      counts.set(raceId, (counts.get(raceId) ?? 0) + 1);
    } else {
      orphans += 1;
    }
  });
  const seenBefore = new Map<string, DocumentSnapshot>();
  before.docs.forEach((d) => seenBefore.set(d.id, d));
  const seenAfter = new Map<string, DocumentSnapshot>();
  after.docs.forEach((d) => seenAfter.set(d.id, d));

  let changed = 0;
  let busy = 0;
  const recountRace = async (raceId: string, registered: number): Promise<void> => {
    const seen = seenBefore.get(raceId);
    const quiet = statsStamp(seen) === statsStamp(seenAfter.get(raceId));
    if (quiet && statsHold(seen, registered)) return;
    try {
      const outcome = quiet ? await writeRecount(raceId, seen, registered) : "conflict";
      if (outcome === "written") {
        changed += 1;
      } else if (outcome === "conflict" && (await recountOneRace(raceId)).written) {
        changed += 1;
      }
    } catch (err) {
      if (!isAborted(err)) throw err;
      busy += 1;
    }
  };
  // Po RECOUNT_PARALLEL závodech zároveň – transakce různých počítadel si
  // nepřekážejí (první běh zakládá počítadlo skoro každému závodu).
  const entries = Array.from(counts.entries());
  for (let i = 0; i < entries.length; i += RECOUNT_PARALLEL) {
    await Promise.all(
      entries
        .slice(i, i + RECOUNT_PARALLEL)
        .map(([raceId, registered]) => recountRace(raceId, registered))
    );
  }

  // Počítadla smazaných závodů pryč (správce smí mazat) – jen když závod
  // pořád neexistuje (mohl mezitím vzniknout a dostat první přihlášku).
  for (const d of after.docs) {
    if (counts.has(d.id)) continue;
    const raceRef = doc(db, "races", d.id);
    const statsRef = raceStatsRef(d.id);
    await runTransaction(db, async (tx) => {
      const race = await tx.get(raceRef);
      if (!race.exists()) tx.delete(statsRef);
    });
  }

  if (busy > 0) {
    throw recountBusy(
      `Přihlášky ${busy === 1 ? "jednoho závodu" : `${busy} závodů`} se během přepočtu pořád měnily – spusťte přepočet znovu.`
    );
  }
  return { races: counts.size, changed, orphans };
}

/**
 * Přepočet obsazenosti JEDNOHO závodu z jeho přihlášek – JEN SPRÁVCE (čte
 * cizí přihlášky; AdminRegistrations při nesouladu počítadla s přihláškami).
 * Počítá dokumenty where raceId včetně starých duplicit – stejně jako
 * recountRaceStats a mazání po jedné přihlášce.
 * Dotaz v transakci spustit nejde: počítadlo se proto přečte PŘED dotazem
 * a v transakci se zapíše, jen když se mezitím nezměnilo (souběžnou
 * přihlášku nebo odhlášení by přepočet jinak přepsal); jinak se počítá znovu.
 * Čte ze serveru; počet, který už sedí, nepřepisuje.
 */
export async function recountRaceStatsFor(raceId: string): Promise<RaceRecountResult> {
  const { registered, previous } = await recountOneRace(raceId);
  return { registered, previous };
}
